import React, { useEffect, useMemo, useState } from "react";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import { Close as CloseIcon } from "@material-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal } from "./index";
import { DOCUMENT_ACTION } from "../redux/action";
import {
  joinSignedConsentUrls,
  parseSignedConsentUrlList,
} from "../utils/efilingFormUtils";

function normalizeDocumentCategory(category) {
  return String(category || "").trim().toLowerCase() === "business"
    ? "business"
    : "individual";
}

function isReceivedClientDocument(doc) {
  // Staff "Received" tab = client-uploaded files (doc_type / folder_type "client").
  const docType = String(doc?.doc_type ?? doc?.docType ?? "")
    .trim()
    .toLowerCase();
  const folderType = String(doc?.folder_type ?? doc?.folderType ?? "")
    .trim()
    .toLowerCase();
  if (docType === "client") return true;
  if (!docType && folderType === "client") return true;
  return false;
}

function matchesDocumentCategory(doc, category) {
  const raw = String(
    doc?.document_category ??
      doc?.documentCategory ??
      doc?.category ??
      "",
  )
    .trim()
    .toLowerCase();
  // If backend didn't stamp category, keep the row (rely on API filter).
  if (!raw) return true;
  return raw === category;
}

function buildConsentDocumentOptions(allClientDocuments, category) {
  const list = Array.isArray(allClientDocuments) ? allClientDocuments : [];
  const seen = new Set();
  const opts = [];
  for (let idx = 0; idx < list.length; idx++) {
    const d = list[idx];
    if (!isReceivedClientDocument(d)) continue;
    if (!matchesDocumentCategory(d, category)) continue;
    const link = String(
      d.document_link ?? d.documentLink ?? d.s3_url ?? d.s3Url ?? "",
    ).trim();
    if (!link || seen.has(link)) continue;
    seen.add(link);
    const name = String(
      d.document_name ?? d.documentName ?? "Document",
    ).trim();
    const folder = String(
      d.folder_name ?? d.folderName ?? d.__folderPath ?? "",
    ).trim();
    const suffix =
      folder && folder.toLowerCase() !== "home" ? ` (${folder})` : "";
    opts.push({ id: link, value: `${name || "Document"}${suffix}` });
  }
  return opts;
}

export default function EfilingConsentField({
  clientId,
  value,
  onChange,
  disabled = false,
  documentCategory = "individual",
}) {
  const dispatch = useDispatch();
  const allClientDocuments = useSelector(
    (state) => state.document?.allClientDocuments || [],
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerUrls, setPickerUrls] = useState([]);
  const category = normalizeDocumentCategory(documentCategory);
  const categoryLabel = category === "business" ? "business" : "individual";

  useEffect(() => {
    if (!clientId) return;
    dispatch(
      DOCUMENT_ACTION.getAllClientDocuments({
        user_id: clientId,
        client_id: clientId,
        document_category: category,
      }),
    );
  }, [clientId, category, dispatch]);

  const consentDocumentOptions = useMemo(
    () => buildConsentDocumentOptions(allClientDocuments, category),
    [allClientDocuments, category],
  );

  const selectedCount = parseSignedConsentUrlList(value).length;

  return (
    <>
      <div className="efiling-consent-field">
        <span className="efiling-consent-field-label">
          Signed consent forms (client documents){" "}
          <span className="efiling-consent-optional">(optional)</span>
        </span>
        <div className="efiling-consent-field-row">
          <Button
            text="Select files…"
            variant="outline"
            ui="transparent"
            disable={disabled || !clientId}
            handleClick={() => {
              setPickerUrls(parseSignedConsentUrlList(value));
              setPickerOpen(true);
            }}
          />
          <span className="efiling-consent-field-summary">
            {selectedCount} file{selectedCount === 1 ? "" : "s"} selected
          </span>
        </div>
        <p className="efiling-consent-field-hint">
          Stored as comma-separated S3 links. In the table, consents show as 1,
          2, … — click a number to preview.
        </p>
      </div>

      <Modal
        title={null}
        open={pickerOpen}
        handleClose={() => setPickerOpen(false)}
        maxWidth
        className="efiling-create-modal efiling-consent-picker-modal"
      >
        <div className="efiling-modal-shell">
          <div className="efiling-modal-header-row">
            <h3 className="efiling-modal-title">
              Select signed consent documents
            </h3>
            <IconButton
              aria-label="close"
              className="efiling-modal-close-btn"
              onClick={() => setPickerOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </div>
          <p className="efiling-modal-category-line">
            Choose one or more received {categoryLabel} documents. They are
            saved as comma-separated S3 links.
          </p>

          <div className="efiling-consent-picker-scroll">
            {consentDocumentOptions.length === 0 ? (
              <p className="efiling-consent-picker-empty">
                No received {categoryLabel} documents with a file link were
                found. Add files under Documents → Received first.
              </p>
            ) : (
              consentDocumentOptions.map((opt) => {
                const checked = pickerUrls.includes(opt.id);
                return (
                  <FormControlLabel
                    key={opt.id}
                    className={
                      "efiling-consent-picker-row" +
                      (checked ? " is-checked" : "")
                    }
                    control={
                      <Checkbox
                        color="primary"
                        checked={checked}
                        onChange={() => {
                          setPickerUrls((prev) => {
                            const next = new Set(prev);
                            if (next.has(opt.id)) next.delete(opt.id);
                            else next.add(opt.id);
                            return [...next];
                          });
                        }}
                      />
                    }
                    label={
                      <span className="efiling-consent-picker-label-text">
                        {opt.value}
                      </span>
                    }
                  />
                );
              })
            )}
          </div>

          <div className="efiling-modal-actions">
            <Button
              text="Cancel"
              ui="transparent"
              variant="outline"
              handleClick={() => setPickerOpen(false)}
            />
            <Button
              text={`Apply (${pickerUrls.length} selected)`}
              disable={consentDocumentOptions.length === 0}
              handleClick={() => {
                onChange(joinSignedConsentUrls(pickerUrls));
                setPickerOpen(false);
              }}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
